import React, { useState } from "react";
import { func, string } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import { parseUrl } from "query-string";
import { WithFacebookLogin, WithGoogleLogin } from "@quintype/components";

import MobileLogo from "../../atoms/MobileLogo";
import Headline from "../../atoms/Headline";
import TextField from "../../atoms/TextField";
import PasswordField from "../../atoms/PasswordField";
import Button from "../../atoms/Button";
import MessageView from "../../atoms/MessageView";
import SocialAuthButton from "../../atoms/SocialAuthButton";
import ProthomFamilyView from "../../atoms/ProthomFamilyView";
import WithSSO from "../../with-sso";

import { register } from "../../helper/api";
import { setLoading } from "../../helper/actions";
import { isValidEmail, validatePassword } from "../../utils/utils";

import "./signup.m.css";

const SERVER_ERROR = "Oops! Something went wrong";

const SignUp = ({ setLoading, googleClientId, facebookId, currentPath, sketchesHost, authHost }) => {
  const [error, setError] = useState(null);
  const [user, setUser] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const params = parseUrl(currentPath);
  const redirectUrl = get(params, ["query", "redirect-url"], sketchesHost);
  const callbackUrl = get(params, ["query", "callback-url"], sketchesHost);

  const setData = e => {
    const userObj = { ...user };
    const fieldName = e.target.name;
    userObj[fieldName] = e.target.value;

    // If error has been set then remove it
    if (e.target.value && userObj[`${fieldName}Error`]) {
      userObj[`${fieldName}Error`] = "";
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    setUser(userObj);
  };

  const onSubmitClick = event => {
    event.preventDefault();

    if (validate()) {
      // true -> field errors, false - no fields errors
      return;
    }

    const userObj = {
      name: user.name,
      email: user.email,
      username: user.email,
      password: user.password,
      "dont-login": false,
      metadata: {}
    };

    setLoading(true);

    register(userObj, callbackUrl, `${callbackUrl}/verify-email`, authHost)
      .then(response => {
        // User needs to validate the email account so navigate to verify email for otp verification
        window.location.href = response["redirect-url"];
        // navigateToPage(`${redirectUrl}verify-email`);
      })
      .catch(err => {
        console.log(err);
        if (err.status === 409) {
          setError(`The email '${userObj.email}' already exists`);
        } else {
          setError(SERVER_ERROR);
        }
      })
      .finally(() => setLoading(false));
  };

  const validate = () => {
    const updatedUser = { ...user };
    let flag = false;

    if (!updatedUser.name) {
      updatedUser.nameError = "Name field cannot be left blank!";
      flag = true;
    } else {
      updatedUser.nameError = "";
    }

    if (!updatedUser.email) {
      updatedUser.emailError = "Email field cannot be left blank!";
      flag = true;
    } else if (!isValidEmail(updatedUser.email)) {
      updatedUser.emailError = "Invalid Email !!";
      flag = true;
    } else {
      updatedUser.emailError = "";
    }

    let pwdError = validatePassword(updatedUser.password);

    if (pwdError) {
      updatedUser.passwordError = pwdError;
      flag = true;
    } else {
      updatedUser.passwordError = "";
    }

    pwdError = validatePassword(updatedUser.confirmPassword);

    if (pwdError) {
      updatedUser.confirmPasswordError = pwdError;
      flag = true;
    } else {
      updatedUser.confirmPasswordError = "";
    }

    if (updatedUser.password && updatedUser.confirmPassword) {
      // If password's exists then check if both are same
      if (updatedUser.password !== updatedUser.confirmPassword) {
        setError("Password and Confirm Password do not match");
        flag = true;
      }
    }

    // If flag is true, there are field error's
    if (flag) {
      setUser(updatedUser); // update state with field errors
    }

    return flag;
  };

  return (
    <form onSubmit={onSubmitClick}>
      <div styleName="container">
        <div styleName="wrapper">
          <Headline text="Create an account" headerLevel={3} headlineType={3} />

          <div styleName="content">
            <div styleName="left-content">
              <WithFacebookLogin sso={true} redirectUrl={redirectUrl} appId={facebookId} scope="email" emailMandatory>
                {({ login, serverSideLoginPath }) => <SocialAuthButton type="fb" href={serverSideLoginPath} />}
              </WithFacebookLogin>

              <WithGoogleLogin
                sso={true}
                redirectUrl={redirectUrl}
                clientId={googleClientId}
                scope="email"
                emailMandatory
              >
                {({ login, serverSideLoginPath }) => (
                  <SocialAuthButton type="google" className="google" href={serverSideLoginPath} />
                )}
              </WithGoogleLogin>

              <span styleName="or-text">OR</span>
              <TextField
                value={user.name}
                error={user.nameError}
                placeholder="Full name"
                name="name"
                inputType="type2"
                onChange={setData}
              />
              <TextField
                value={user.email}
                error={user.emailError}
                placeholder="Email"
                type="email"
                name="email"
                inputType="type2"
                onChange={setData}
              />
              <PasswordField
                value={user.password}
                error={user.passwordError}
                placeholder="Password"
                name="password"
                onChange={setData}
              />
              <PasswordField
                value={user.confirmPassword}
                error={user.confirmPasswordError}
                placeholder="Confirm Password"
                name="confirmPassword"
                onChange={setData}
              />

              <span styleName="password-message">Use 8 or more characters with a mix of letters & numbers</span>

              <MessageView type="error" text={error} />

              <Button content="REGISTER" type="submit" buttonType="type3" />

              <WithSSO
                ssoHost={authHost}
                signInPath="/sign-in-page"
                redirectUrl={redirectUrl}
                callbackUrl={callbackUrl}
              >
                {({ signInHref }) => (
                  <span styleName="old-user">
                    Already a member?&nbsp;
                    <a styleName="login" href={signInHref}>
                      Login
                    </a>
                  </span>
                )}
              </WithSSO>
            </div>

            <div styleName="right-content">
              <MobileLogo />
              <div styleName="signup-message">
                One account. All of Prothom Alo <br /> working for you
              </div>
            </div>
          </div>
        </div>

        <div styleName="terms-privacy">
          By proceeding, you agree to the Prothom Alo’s <br /> <a>Terms of Use</a> and <a>Privacy Policy</a>
        </div>
        <ProthomFamilyView />
      </div>
    </form>
  );
};

SignUp.propTypes = {
  setLoading: func,
  checkForMemberUpdated: func,
  googleClientId: string,
  facebookId: string,
  currentPath: string,
  sketchesHost: string,
  authHost: string,
  navigateToPage: func
};

const mapStateToProps = state => ({
  googleClientId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "google"], ""),
  facebookId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "fb"], ""),
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null)
});

const mapDispatchToProps = dispatch => ({
  setLoading: isLoading => dispatch(setLoading(isLoading)),
  navigateToPage: url => global.app.navigateToPage(dispatch, url)
});

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
